import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Users, XCircle, Pencil } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  useApprovalDelegations,
  useCreateApprovalDelegation,
  useUpdateApprovalDelegation,
  useRevokeApprovalDelegation,
} from '@/hooks/use-payroll';
import { useOrgToday, useUsers } from '@/hooks/use-settings';
import { useProjects } from '@/hooks/use-projects';
import { useAuth } from '@/contexts/auth-context';
import type { ApprovalDelegation, CreateApprovalDelegationPayload, ApprovalDelegationScope } from '@/lib/types';

type DelegationTab = 'given' | 'received';

const getEmptyForm = (
  orgToday: string
): Omit<CreateApprovalDelegationPayload, 'delegator'> => ({
  delegate: 0,
  start_date: orgToday,
  end_date: null,
  scope: 'all_timesheets',
});

export function DelegationsPage() {
  const orgToday = useOrgToday();
  const { user } = useAuth();
  const [tab, setTab] = useState<DelegationTab>('given');
  const { data: delegations, isLoading } = useApprovalDelegations({
    direction: tab,
    active_only: 'false',
  });
  const { data: usersData } = useUsers({ is_active: true });
  const { data: projectsData } = useProjects({ status: 'active' });
  const createDelegation = useCreateApprovalDelegation();
  const updateDelegation = useUpdateApprovalDelegation();
  const revokeDelegation = useRevokeApprovalDelegation();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(() => getEmptyForm(orgToday));

  const users = usersData?.results ?? [];
  const projects = projectsData?.results ?? [];

  const handleNew = () => {
    setEditingId(null);
    setForm(getEmptyForm(orgToday));
    setShowForm(true);
  };

  const handleEdit = (delegation: ApprovalDelegation) => {
    setEditingId(delegation.id);
    setForm({
      delegate: delegation.delegate,
      start_date: delegation.start_date,
      end_date: delegation.end_date,
      scope: delegation.scope,
      project: delegation.project,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (editingId) {
      await updateDelegation.mutateAsync({ id: editingId, ...form });
    } else {
      await createDelegation.mutateAsync({
        delegator: user.id,
        ...form,
      });
    }
    setShowForm(false);
    setEditingId(null);
    setForm(getEmptyForm(orgToday));
  };

  const handleRevoke = (id: number) => {
    if (window.confirm('Revoke this delegation?')) {
      revokeDelegation.mutate(id);
    }
  };

  const items = Array.isArray(delegations) ? delegations : [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" asChild>
            <Link to="/payroll">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Approval Delegations
            </h1>
            <p className="text-gray-600 mt-1">
              Delegate timesheet &amp; expense approval authority
            </p>
          </div>
        </div>
        {tab === 'given' && (
          <Button onClick={handleNew}>
            <Plus className="h-4 w-4 mr-2" />
            New Delegation
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button
          variant={tab === 'given' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setTab('given')}
        >
          Delegations Given
        </Button>
        <Button
          variant={tab === 'received' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setTab('received')}
        >
          Delegations Received
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingId ? 'Edit Delegation' : 'New Delegation'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label>Delegate (receives authority) *</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.delegate || ''}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        delegate: Number(e.target.value),
                      }))
                    }
                    required
                  >
                    <option value="">Select a user...</option>
                    {users
                      .filter((u) => u.id !== user?.id)
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.username})
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, start_date: e.target.value }))
                    }
                    required
                  />
                </div>
                <div>
                  <Label>End Date (leave blank for open-ended)</Label>
                  <Input
                    type="date"
                    value={form.end_date ?? ''}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        end_date: e.target.value || null,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label>Scope</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.scope}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        scope: e.target.value as ApprovalDelegationScope,
                        project: e.target.value === 'all_timesheets' ? null : p.project,
                      }))
                    }
                  >
                    <option value="all_timesheets">All Timesheets</option>
                    <option value="project_specific">Project Specific</option>
                  </select>
                </div>
                {form.scope === 'project_specific' && (
                  <div>
                    <Label>Project *</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={form.project ?? ''}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          project: e.target.value ? Number(e.target.value) : null,
                        }))
                      }
                      required
                    >
                      <option value="">Select a project...</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={
                    createDelegation.isPending || updateDelegation.isPending
                  }
                >
                  {editingId ? 'Update Delegation' : 'Create Delegation'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">
              Loading delegations...
            </div>
          ) : !items.length ? (
            <div className="p-8 text-center text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>
                {tab === 'given'
                  ? 'No delegations given'
                  : 'No delegations received'}
              </p>
              {tab === 'given' && (
                <p className="text-sm mt-1">
                  Create a delegation to grant approval authority to another user
                </p>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {items.map((d) => (
                <div
                  key={d.id}
                  className="p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900">
                          {tab === 'given'
                            ? `To: ${d.delegate_username}`
                            : `From: ${d.delegator_username}`}
                        </h3>
                        <Badge
                          variant={d.is_active ? 'default' : 'secondary'}
                        >
                          {d.is_active ? 'Active' : 'Revoked'}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
                        <span>From: {d.start_date}</span>
                        <span>
                          To: {d.end_date ?? 'Open-ended'}
                        </span>
                        <span>
                          Scope:{' '}
                          {d.scope === 'all_timesheets'
                            ? 'All Timesheets'
                            : `Project: ${d.project_name ?? 'N/A'}`}
                        </span>
                      </div>
                    </div>
                    {tab === 'given' && d.is_active && (
                      <div className="flex gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(d)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRevoke(d.id)}
                        >
                          <XCircle className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
